// frontend/src/components/billing/manage-subscription-dialog.tsx
// Read DESIGN.md before modifying this file.
// Task 7 (Phase 4, non-destructive deflection): single usage-tier cancel surface.
// On open, fetches GET /organizations/:orgId/billing/manage-subscription and
// renders one cancel row for
// the usage plan. The deflection step offers a non-destructive ladder: keep the
// current plan, step to a cheaper paid rung, or drop to Free. Nothing is deleted.
import { useEffect, useState } from 'react';
import { Info, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { getV2Plan, isV2PlanSlug, V2_PLANS } from '@/lib/plans';
import {
  useManageSubscriptionQuery,
  useUsageTierChange,
} from '@/components/billing/queries';
import { toast } from 'sonner';

/** Which entry card opened the deflection step. 'downgrade' steps to a cheaper
 *  paid rung; 'cancel' drops to Free. */
type DeflectTarget = 'downgrade' | 'cancel';

/** Upgrade ladder, cheapest-first. Mirrors lib/plans PLAN_ORDER. */
const TIER_ORDER = ['free', 'starter', 'growth', 'pro'] as const;
const TIER_META: Record<string, { name: string; priceCents: number }> = {
  starter: { name: 'Build', priceCents: 1200 },
  growth: { name: 'Scale', priceCents: 2400 },
  pro: { name: 'Business', priceCents: 3900 },
};

function formatPrice(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(iso));
}

/** A selectable choice in the deflection wizard; a full-width card button. */
function ChoiceCard({
  title,
  price,
  description,
  tone = 'default',
  busy,
  disabled,
  onClick,
}: {
  title: string;
  price?: string;
  description?: string;
  tone?: 'default' | 'destructive';
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex w-full flex-col gap-0.5 rounded-2xl border bg-card p-4 text-left transition-colors',
        'hover:border-ring/60 hover:bg-muted/40',
        'focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
        'disabled:pointer-events-none disabled:opacity-50',
        tone === 'destructive' && 'border-destructive/30 bg-destructive/5 hover:bg-destructive/10',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            'flex items-center gap-1.5 text-sm font-medium',
            tone === 'destructive' && 'text-destructive',
          )}
        >
          {busy && <Loader2 className="size-3.5 animate-spin" />}
          {title}
        </span>
        {price && <span className="text-sm text-muted-foreground">{price}</span>}
      </div>
      {description && <span className="text-xs text-muted-foreground">{description}</span>}
    </button>
  );
}

export function ManageSubscriptionDialog({
  orgId,
  billingInterval,
  open,
  onOpenChange,
  onDowngrade,
}: {
  orgId: string | undefined;
  /** The subscription's billing cadence. Required by the usage-tier route. */
  billingInterval: 'monthly' | 'annual';
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Hand a chosen cheaper paid rung to the parent's shared tier-switch
   *  approval modal (period-end Downgrade copy). */
  onDowngrade: (slug: string, name: string) => void;
}) {
  // Read model, refetched on every open (staleTime 0); a reopen shows the
  // spinner while the fresh load is in flight.
  const modelQuery = useManageSubscriptionQuery(orgId, open);
  // Cancel/downgrade writes; on success the hook invalidates the shared
  // subscription key so the Plans page refetches (replaces the old onDone).
  const tierChange = useUsageTierChange(orgId ?? null);
  const model = modelQuery.data ?? null;
  const loading = modelQuery.isFetching;
  // Which action is in flight, by key; only the clicked card spins while
  // every card disables. A plain boolean spun whatever card was wired busy.
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const busy = busyKey !== null;
  const [deflect, setDeflect] = useState<DeflectTarget | null>(null);

  // Reset any in-progress deflection step on close so a reopen starts fresh.
  // Render-time state adjustment (not an effect) so every close path counts,
  // including the parent flipping `open` directly.
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open) setDeflect(null);
  }

  // Surface a failed model load as a toast (once per failed fetch; retry is
  // off, so each open produces at most one error). 4xx keeps the backend's
  // clean message; 5xx/network shows the load-specific copy.
  useEffect(() => {
    if (!modelQuery.error) return;
    const status = (modelQuery.error.details as { status?: number }).status;
    toast.error(
      typeof status === 'number' && status < 500
        ? modelQuery.error.userMessage ?? 'Failed to load your subscription.'
        : 'Failed to load your subscription.',
    );
  }, [modelQuery.error]);

  // Step the usage tier (POST /organizations/:orgId/billing/usage-tier).
  // planSlug 'free' drops to the
  // free baseline. Other slugs switch to a cheaper paid rung. Scheduled at period end.
  function changeTier(planSlug: string, successMsg: string, key = planSlug) {
    setBusyKey(key);
    tierChange.mutate(
      { planSlug, billingInterval },
      {
        onSuccess: () => {
          onOpenChange(false);
          toast.success(successMsg);
        },
        onError: (err) =>
          toast.error(err.userMessage ?? 'Something went wrong. Please try again.'),
        onSettled: () => setBusyKey(null),
      },
    );
  }

  const usageSlug = model?.usage?.slug;
  const onV2 = usageSlug ? isV2PlanSlug(usageSlug) : false;
  // Paid rungs strictly below the current tier, highest-first (Business steps
  // down through Scale then Build; Scale offers Build; Build has none).
  // AIT-494: v2 gets the same ladder from the v2 catalog — downgrades and
  // cancel ride the same usage-tier endpoint as legacy now.
  const lowerPaidTiers = ((): string[] => {
    // AIT-738: a custom plan changes tier through us only; Cancel stays.
    if (model?.usage?.custom) return [];
    if (onV2) {
      const order = V2_PLANS.map((p) => p.slug as string);
      const idx = order.indexOf(usageSlug as string);
      return idx <= 0 ? [] : order.slice(0, idx).reverse();
    }
    const idx = TIER_ORDER.indexOf((usageSlug ?? 'free') as (typeof TIER_ORDER)[number]);
    if (idx <= 1) return [];
    return TIER_ORDER.slice(1, idx).reverse();
  })();
  /** Name + price for a ladder rung, quoted in the org's billing interval —
   *  an annual org sees the annual charge, not a monthly number that Stripe
   *  will never bill (CodeRabbit PR #260). Legacy tiers stay monthly-only. */
  function metaFor(slug: string): { name: string; priceCents: number; per: 'mo' | 'yr' } {
    const annual = billingInterval === 'annual';
    const v2 = getV2Plan(slug);
    if (v2) {
      return annual
        ? { name: v2.name, priceCents: v2.annualPriceInCents, per: 'yr' }
        : { name: v2.name, priceCents: v2.priceInCents, per: 'mo' };
    }
    return { ...TIER_META[slug], per: 'mo' };
  }

  /** Downgrade chooser, reached only when more than one cheaper paid rung
   *  exists (Business). Each rung hands off to the shared tier-switch approval
   *  modal. A single rung skips this screen entirely (see the entry card). */
  function renderDowngrade() {
    return (
      <>
        <p className="text-sm text-muted-foreground">
          Step down to a cheaper paid plan. Keep every workspace and message;
          the cheaper plan's caps apply. Nothing is deleted.
        </p>
        <div className="flex flex-col gap-2">
          <ChoiceCard
            title="Keep My Plan"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          />
          {lowerPaidTiers.map((slug) => {
            const meta = metaFor(slug);
            return (
              <ChoiceCard
                key={slug}
                title={`Switch To ${meta.name}`}
                price={`${formatPrice(meta.priceCents)}/${meta.per}`}
                description={`Keep every workspace and message. ${meta.name}'s caps apply.`}
                disabled={busy}
                onClick={() => onDowngrade(slug, meta.name)}
              />
            );
          })}
        </div>
      </>
    );
  }

  /** Cancel step: deflect toward a cheaper paid rung first, then offer the
   *  drop to Free. Non-destructive — nothing is deleted on any rung. */
  function renderCancel() {
    return (
      <>
        <p className="text-sm text-muted-foreground">
          {onV2
            ? 'Canceling ends your plan at the end of the billing period. Your channels pause until you pick a new plan. Nothing is deleted.'
            : 'Keep everything. Downgrading to Free drops you to 2,000 messages per month and 1 GB media. Forwarding pauses only if you exceed the free cap. Nothing is deleted.'}
        </p>
        <div className="flex flex-col gap-2">
          <ChoiceCard
            title="Keep My Plan"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          />
          {lowerPaidTiers.map((slug) => {
            const meta = metaFor(slug);
            return (
              <ChoiceCard
                key={slug}
                title={`Switch To ${meta.name}`}
                price={`${formatPrice(meta.priceCents)}/${meta.per}`}
                description={`Keep every workspace and message. ${meta.name}'s caps apply.`}
                busy={busyKey === slug}
                disabled={busy}
                onClick={() =>
                  changeTier(
                    slug,
                    `Your plan will switch to ${meta.name} at the end of your billing period.`,
                  )
                }
              />
            );
          })}
          {onV2 ? (
            // AIT-494: real self-serve cancel — 'free' is the shared
            // cancel-at-period-end path (no free price exists on v2; the
            // backend ends the whole subscription at period end).
            <ChoiceCard
              tone="destructive"
              title="Cancel Plan"
              description="Your plan ends at the end of the billing period. Channels pause until you pick a plan. Nothing is deleted."
              busy={busyKey === 'free'}
              disabled={busy}
              onClick={() => changeTier('free', 'Your plan will be cancelled at the end of your billing period.')}
            />
          ) : (
          <ChoiceCard
            tone="destructive"
            title="Cancel To Free"
            description="2,000 messages per month and 1 GB media. Nothing is deleted."
            busy={busyKey === 'free'}
            disabled={busy}
            onClick={() => changeTier('free', 'Your plan will be cancelled at the end of your billing period.')}
          />
          )}
        </div>
      </>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Manage Subscription</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading subscription...
          </div>
        ) : !model ? null : deflect === 'downgrade' ? (
          renderDowngrade()
        ) : deflect === 'cancel' ? (
          renderCancel()
        ) : (
          <div className="flex flex-col gap-2">
            {model.pending && (
              <Alert className="border-blue-500/30 bg-blue-500/5">
                <Info className="text-blue-500" />
                <AlertDescription>
                  {model.pending.kind === 'usageDowngrade'
                    ? `A plan change is scheduled for ${formatDate(model.pending.effectiveAt)}.`
                    : `A cancellation is scheduled for ${formatDate(model.pending.effectiveAt)}.`}
                </AlertDescription>
              </Alert>
            )}

            {model.usage && (
              <>
                {/* Downgrade first (only when a cheaper paid rung exists below
                    the current tier); Cancel second. Build, the lowest paid
                    tier, has no cheaper rung so it shows Cancel alone. */}
                {lowerPaidTiers.length > 0 && (
                  <ChoiceCard
                    title="Downgrade Plan"
                    description="Step down to a cheaper plan."
                    disabled={busy}
                    // One rung (Scale -> Build): skip the chooser, go straight to
                    // the shared approval modal. Two+ (Business): show the chooser.
                    onClick={() => {
                      if (lowerPaidTiers.length === 1) {
                        const slug = lowerPaidTiers[0];
                        onDowngrade(slug, metaFor(slug).name);
                      } else {
                        setDeflect('downgrade');
                      }
                    }}
                  />
                )}
                <ChoiceCard
                  tone="destructive"
                  title="Cancel Plan"
                  description={
                    onV2
                      ? `End ${model.usage.name} at the end of your billing period. Nothing is deleted.`
                      : `Drop ${model.usage.name} to Free. Nothing is deleted.`
                  }
                  disabled={busy}
                  onClick={() => setDeflect('cancel')}
                />
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
